/**
 *  1:1 문의 목록
 *
 *  @author  Bruce.Cheong (2026-05-06)
 *
 */

/** External Imports */

/** Internal import */
import CSS                  from    "./List.module.css";

/** 목록 테이블 컬럼 정의 */
type it_ListField = "post_contents" | "answer_status" | "post_insert_dtm";

interface it_ListTitleItem {
    field: it_ListField;
    title: string;
}

/** 목록과 상세 팝업에서 함께 사용하는 게시물 모델 */
interface it_PostItem {
    post_pk: number;
    group_name: string;
    post_title: string;
    post_contents: string;
    post_insert_dtm: string;
    answer_pk: number | null;
    answer_contents: string | null;
    answer_insert_dtm: string | null;
    answer_status: "답변완료" | "답변대기";
    [key: string]: unknown;
}

interface it_ListProps {
    listTitle: it_ListTitleItem[];
    listData?: it_PostItem[];
    rowClickHandler?: (iPost: it_PostItem) => void;
}

function List(iProps: it_ListProps) {
    /** field 이름과 CSS Module selector 이름을 맞춰 컬럼별 스타일을 공유한다. */
    const getColumnClassName = (field: it_ListField) => {
        return CSS[field];
    };

    /** 컬럼 정의에 맞춰 셀 UI를 분기해 목록 테이블을 재사용 가능하게 유지한다. */
    const renderCell = (item: it_PostItem, column: it_ListTitleItem) => {
        switch (column.field) {
            case "answer_status":
                return (
                    <span
                        className={[
                            CSS.StatusBadge,
                            item[column.field] === "답변완료" ? CSS.StatusDone : CSS.StatusPending,
                        ].join(" ")}
                    >
                        {item[column.field]}
                    </span>
                );
            default:
                return (
                    <>
                        {item[column.field]}
                    </>
                );
        }
    };

    return (
        <div className={CSS.List}>
            <table className={CSS.Table}>
                {/* 헤더와 본문이 같은 컬럼 폭 규칙을 쓰도록 colgroup에도 동일한 class를 준다. */}
                <colgroup>
                    {iProps.listTitle.map((item) => {
                        return (
                            <col key={item.field} className={getColumnClassName(item.field)} />
                        );
                    })}
                </colgroup>

                <thead className={CSS.TableHead}>
                    <tr>
                        {iProps.listTitle.map((item) => {
                            return (
                                <th key={item.field} className={getColumnClassName(item.field)}>
                                    {item.title}
                                </th>
                            );
                        })}
                    </tr>
                </thead>

                <tbody>
                    {/* 행 클릭으로 상세 라우트와 팝업 열기 흐름을 연결한다. */}
                    {iProps.listData?.map((item, index) => {
                        return (
                            <tr
                                key={item.post_pk || index}
                                className={iProps.rowClickHandler ? CSS.ClickableRow : ""}
                                onClick={() => {
                                    iProps.rowClickHandler?.(item);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        iProps.rowClickHandler?.(item);
                                    }
                                }}
                                tabIndex={iProps.rowClickHandler ? 0 : -1}
                            >
                                {iProps.listTitle.map((iRow) => {
                                    return (
                                        <td
                                            key={`${item.post_pk}-${iRow.field}`}
                                            className={getColumnClassName(iRow.field)}
                                            data-label={iRow.title}
                                        >
                                            {renderCell(item, iRow)}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default List;
